// Content Service - Fetch content from public/content.json
import { 
  portfolioProjects, 
  categories,
  services as mockServices,
  testimonials,
  stats
} from '../data/mock';

let cachedContent = null;

/**
 * Fetch content from content.json
 */
export const fetchContent = async () => {
  if (cachedContent) {
    return cachedContent;
  }

  try {
    const response = await fetch('/content.json');
    
    if (!response.ok) {
      throw new Error('Content file not found');
    }
    
    const data = await response.json();
    cachedContent = data;
    return data;
  } catch (error) {
    console.warn('Could not load content.json, using fallback data:', error);
    return null;
  }
};

/**
 * Get HomePage content
 */
export const getHomePage = async () => {
  const content = await fetchContent();
  
  if (content && content.homePage) {
    return content.homePage;
  }
  
  // Fallback
  return {
    heroTitle: 'ADK Creative',
    heroSubtitle: 'Grafik Tasarım & Marka Kimliği',
    heroButtonText: 'Portfolyoyu İncele',
    heroButtonLink: '#portfolio',
    aboutTitle: 'Markaları Dijital Dünyada Güçlendiriyoruz',
    aboutText: 'ADK Creative, markaların dijital dünyada daha güçlü görünmesi için modern, sade ve hedef odaklı tasarımlar üretir.',
  };
};

/**
 * Get Services
 */
export const getServices = async () => {
  const content = await fetchContent();
  
  if (content && content.services && content.services.length > 0) {
    return content.services;
  }
  
  // Fallback
  return mockServices;
};

/**
 * Get Portfolio projects
 */
export const getPortfolio = async () => {
  const content = await fetchContent();
  
  if (content && content.portfolio && content.portfolio.length > 0) {
    return content.portfolio;
  }
  
  // Fallback
  return portfolioProjects;
};

/**
 * Get Portfolio categories
 */
export const getPortfolioCategories = async () => {
  const projects = await getPortfolio();
  
  // Extract unique categories
  const uniqueCategories = ['Tümü', ...new Set(projects.map(p => p.category))];
  return uniqueCategories;
};

/**
 * Get Pricing packages
 */
export const getPricing = async () => {
  const content = await fetchContent();
  
  if (content && content.pricing && content.pricing.length > 0) {
    return content.pricing;
  }
  
  // Fallback - return packages from PricingSection component
  return [
    {
      id: 1,
      name: 'Mini',
      subtitle: 'Düzen Kur',
      price: '3.900',
      period: 'tek sefer',
      icon: 'Sparkles',
      popular: false,
      features: [
        '4 Post tasarım',
        '8 Story şablonu',
        'Profil düzeni (bio + highlight kapak)',
        '1 revize turu'
      ],
      color: '#d4af37'
    },
    {
      id: 2,
      name: 'Başlangıç',
      subtitle: 'Aylık Set',
      price: '6.900',
      period: 'aylık',
      icon: 'TrendingUp',
      popular: false,
      features: [
        '8 Post tasarım',
        '12 Story tasarım',
        '2 Reels kapak / thumbnail',
        '1 revize turu'
      ],
      color: '#d4af37'
    },
    {
      id: 3,
      name: 'Büyüme',
      subtitle: 'Daha Görünür',
      price: '10.900',
      period: 'aylık',
      icon: 'Zap',
      popular: true,
      features: [
        '12 Post tasarım',
        '20 Story tasarım',
        '4 Reels kapak',
        '1 kampanya afişi + 2 boyut (post/story)'
      ],
      color: '#d4af37'
    },
    {
      id: 4,
      name: 'Pro',
      subtitle: 'Kampanya + Reklam',
      price: '16.900',
      period: 'aylık',
      icon: 'Crown',
      popular: false,
      features: [
        '16 Post tasarım',
        '30 Story tasarım',
        '6 Reels kapak',
        '2 kampanya seti (4 boyut varyasyonu)'
      ],
      color: '#d4af37'
    },
    {
      id: 5,
      name: 'Kurumsal Kimlik',
      subtitle: 'Marka Seti',
      price: '14.900',
      period: 'tek sefer',
      icon: 'Briefcase',
      popular: false,
      features: [
        'Logo tasarımı / düzenleme',
        'Renk–font sistemi + mini kılavuz',
        'Kartvizit + antet (PDF)',
        'Şablon seti: 6 post + 6 story'
      ],
      color: '#d4af37'
    },
    {
      id: 6,
      name: 'Sürekli Destek',
      subtitle: 'Tasarım Bankası',
      price: '21.900',
      period: 'aylık',
      icon: 'Infinity',
      popular: false,
      features: [
        'Aylık 25 tasarım hakkı (post/story/afiş/banner)',
        'Reklam kreatifleri (boyut setleri)',
        'Öncelikli teslim + 3 revize turu',
        'Dosya arşivi & şablon düzeni'
      ],
      color: '#d4af37'
    }
  ];
};

/**
 * Get FAQs
 */
export const getFAQs = async () => {
  const content = await fetchContent();
  
  if (content && content.faq && content.faq.length > 0) {
    return content.faq;
  }
  
  // Fallback - return FAQs from FAQSection component
  return [
    {
      id: 1,
      question: 'Hangi dosya formatlarında teslim yapılır?',
      answer: 'Tüm tasarımlar PNG, JPG ve PDF formatında teslim edilir. İhtiyaç durumunda AI, PSD gibi kaynak dosyalar da sağlanabilir.'
    },
    {
      id: 2,
      question: 'Revize süreci nasıl işler?',
      answer: 'Her pakette belirtilen revize hakkınız vardır. Revize talepleri tasarımın ilk teslimatından sonraki 48 saat içinde iletilmelidir. Revizyeler genellikle 24-48 saat içinde tamamlanır.'
    },
    {
      id: 3,
      question: 'Teslimat süresi ne kadar?',
      answer: 'Mini paket 3-5 iş günü, aylık paketler aybaşında toplu teslim edilir. Kurumsal kimlik projesi 7-10 iş günü, Sürekli Destek paketi ise aylık periyotlarla çalışır.'
    },
    {
      id: 4,
      question: 'Ödeme nasıl yapılır?',
      answer: 'Havale/EFT, kredi kartı veya PayTR üzerinden ödeme yapabilirsiniz. Aylık paketlerde peşin ödeme zorunludur. Büyük projelerde %50 ön ödeme alınır.'
    }
  ];
};

/**
 * Get Contact info
 */
export const getContact = async () => {
  const content = await fetchContent();
  
  if (content && content.contact) {
    return content.contact;
  }
  
  // Fallback
  return {
    phone: '+90 555 123 4567',
    email: 'ahmetdemirkesen81@gmail.com',
    address: 'Bartın, Türkiye',
    instagramUrl: 'https://instagram.com/adkcreative',
    whatsappUrl: '',
    linkedinUrl: '',
    behanceUrl: ''
  };
};

/**
 * Clear cache (for development)
 */
export const clearCache = () => {
  cachedContent = null;
};

export default {
  fetchContent,
  getHomePage,
  getServices,
  getPortfolio,
  getPortfolioCategories,
  getPricing,
  getFAQs,
  getContact,
  clearCache,
};
